import { Building2, User } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaChoiceCards } from "@/shared/components/forms/ColocaChoiceCards";
import useTranslations from "@/shared/lang/useTranslations";

import { useIsMexicanOrganization } from "../hooks/useIsMexicanOrganization";
import type { VerificationPurpose } from "../types/verification-api.types";

export interface VerificationTypeChoiceProps {
  onChoose: (purpose: VerificationPurpose) => void;
}

/**
 * First screen of the onboarding when the organization has no corporate identity yet: verify as a natural
 * person (KYC) or as a company (KYB). The choice is made once — it becomes the `type` of the corporate
 * identity created two steps later, and the gate reads the same verification either way — so the copy says
 * so.
 */
export function VerificationTypeChoice({ onChoose }: VerificationTypeChoiceProps) {
  const { t } = useTranslations("verification");
  const [value, setValue] = useState<"" | VerificationPurpose>("");
  const isMexico = useIsMexicanOrganization();

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold text-foreground">{t("choose_title")}</h2>
        <p className="text-sm text-muted-foreground">{t("choose_description")}</p>
      </div>

      <ColocaChoiceCards
        aria-label={t("choose_title")}
        value={value}
        onValueChange={(next) => {
          if (next === "KYC" || next === "KYB") setValue(next);
        }}
        columns={2}
        items={[
          {
            value: "KYC",
            label: t(isMexico ? "choose_person_title_mx" : "choose_person_title"),
            description: t("choose_person_description"),
            icon: <User className="size-5" aria-hidden="true" />,
          },
          {
            value: "KYB",
            label: t("choose_business_title"),
            description: t("choose_business_description"),
            icon: <Building2 className="size-5" aria-hidden="true" />,
          },
        ]}
      />

      <p className="text-xs text-muted-foreground">{t("choose_note")}</p>

      <ColocaButton
        variant="primary"
        className="w-full"
        disabled={value === ""}
        onClick={() => {
          if (value) onChoose(value);
        }}
      >
        {t("choose_cta")}
      </ColocaButton>
    </div>
  );
}
